import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["picture", "image", "video"]

  connect() {
    this.abort = new AbortController()
    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.motion.addEventListener("change", () => {
      if (this.motion.matches) this.fallback()
    }, { signal: this.abort.signal })
    this.prepare()
  }

  async prepare() {
    const { signal } = this.abort
    if (this.motion.matches || navigator.connection?.saveData) return

    // decode() resolves before paint. Two frames give the image a paint before
    // the video can compete for the network and main thread.
    try { await this.imageTarget.decode() } catch { return }
    if (signal.aborted) return
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
    if (signal.aborted) return
    if (document.readyState !== "complete") {
      await new Promise(resolve => window.addEventListener("load", resolve, { once: true, signal }))
    }
    if (signal.aborted) return

    this.observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return
      this.observer.disconnect()
      if (signal.aborted || this.motion.matches || navigator.connection?.saveData) return
      const video = this.videoTarget
      video.muted = true
      video.querySelectorAll("source[data-src]").forEach(source => { source.src = source.dataset.src })
      video.load()
      video.play().catch(() => { if (!signal.aborted) this.fallback() })
    })
    this.observer.observe(this.element)
  }

  reveal() {
    if (this.motion.matches) return this.fallback()
    this.videoTarget.classList.remove("invisible")
    this.pictureTarget.classList.add("opacity-0")
  }

  fallback() {
    this.videoTarget.pause()
    this.videoTarget.classList.add("invisible")
    this.pictureTarget.classList.remove("opacity-0")
  }

  disconnect() {
    this.abort.abort()
    this.observer?.disconnect()
    this.fallback()
    this.videoTarget.querySelectorAll("source").forEach(source => source.removeAttribute("src"))
    this.videoTarget.load()
  }
}
